import {KatalWeblabClient, Weblab, BrowserStorageCache} from '@amzn/katal-weblab';
import {KatalWebSellerCentralClient} from '@amzn/katal-weblab-sellercentral';

import WeblabTreatment from "src/model/WeblabTreatment";
import RevCalMetricsUtil from "src/utils/RevCalMetricsUtil";
import {DIGITAL_SERVICES_FEE_WEBLAB} from "src/config/Config";

/**
 * Uses the browser's built in Session Storage to provide a cache that can survive page reloads and navigations.
 * This cache will be cleared once the browser tab or window is closed.
 */
const cacheTTLSeconds = 10 * 60; // 10 minutes * 60 seconds

/**
 * This information is required from each weblab when using the SellerCentralWeblabClient to call the Mons Proxy.
 * The proxy works by reading the Mons Context for you and setting the CustomerId, MerchantId, and Session.
 */
enum WeblabType {
    CUSTOMER = 'customer',
    MERCHANT = 'merchant',
    SESSION = 'session'
}

export const weblabClient = new KatalWeblabClient({
    metricsPublisher: RevCalMetricsUtil.getMetricsPublisher(),
    weblabProxy: new KatalWebSellerCentralClient(),
    treatmentCache: new BrowserStorageCache('RevCal', cacheTTLSeconds)
});

export function getWeblabList(): Weblab[] {
    return [
        {
            defaultTreatment: 'C',
            id: DIGITAL_SERVICES_FEE_WEBLAB,
            weblabType: WeblabType.CUSTOMER
        },
        {
            defaultTreatment: 'C',
            id: 'REV_CAL_IMPORT_FEES_802564',
            weblabType: WeblabType.CUSTOMER
        },
        {
            // weblab to enable low inventory level surcharge for fulfillment fee on RC/IRC
            defaultTreatment: 'C',
            id: 'REV_CAL_2_LOW_INVENTORY_824500',
            weblabType: WeblabType.CUSTOMER
        },
        {
            defaultTreatment: 'C',
            id: 'RC_IRC_PROVIDED_COST_851922',
            weblabType: WeblabType.CUSTOMER
        },
        {
            defaultTreatment: 'C',
            id: 'RC_IPS_CHANGE_872302',
            weblabType: WeblabType.CUSTOMER
        },
        {
            // weblab to enable dstr fee-explainer on RC/IRC
            defaultTreatment: 'C',
            id: 'FEE_EXPLAINER_DSTR_ON_RC_818955',
            weblabType: WeblabType.CUSTOMER
        }
    ];
}

export function callWeblabTreatments() {
    let weblabList = getWeblabList();

    return weblabClient.getTreatments(weblabList).then(treatments => {
        let weblabTreatments: WeblabTreatment[] = [];

        weblabList.forEach(weblab => {
            if (treatments[weblab.id] && treatments[weblab.id].treatmentValue) {
                weblabTreatments.push({ weblabId: weblab.id, treatment: treatments[weblab.id].treatmentValue! });
                if (weblab.weblabType === WeblabType.MERCHANT || weblab.weblabType === WeblabType.CUSTOMER) {
                    treatments[weblab.id].recordTrigger();
                }
            } else {
                weblabTreatments.push({weblabId: weblab.id, treatment: 'C'});
            }
        });

        return weblabTreatments;
    });
}

export const getWeblab = (availableWeblabs: WeblabTreatment[], name: string): string => {
    let weblab = availableWeblabs.find(weblab => weblab.weblabId === name);

    return weblab ? weblab.treatment : 'C';
};
